// Explanation describing ABN validation can be found here:
//
// https://abr.business.gov.au/Help/AbnFormat
const ABN_WEIGHTS = [10, 1, 3, 5, 7, 9, 11, 13, 15, 17, 19];

/**
 * Takes a string and returns true if it is a valid abn OR an empty string
 *
 * Removes any spaces in the number
 *
 * @argument value - The number to test if it is an abn
 * @return boolean - True if the number is a valid ABN number or empty. Otherwise false.
 *
 * @example
 *
 *   isABN('51 824 753 556') === true
 *   isABN('51 824 753 557') === false
 *   isABN('51824753556') === true
 *   isABN('51824753557') === false
 *   isABN('') === true
 *   isABN(' ') === false
 *   isABN('abc') === false
 *
 */
export const isABN = (value: string) => {
  // The reasoning of returning true on a blank string is so that it can be used
  // with yup.test. If blanks are invalid, use yup's .required() method
  if (!value) {
    return true;
  }
  const digits = value.split('').reduce((acc, cur) => {
    if (acc === null || cur === ' ') {
      return acc;
    }
    const num = parseInt(cur, 10);
    if (Number.isNaN(num)) {
      return null;
    }
    return [...acc, num];
  }, []);

  // We've received something other than a number
  if (digits === null) {
    return false;
  }

  if (digits.length !== 11) {
    return false;
  }

  digits[0] -= 1;
  const weighted = digits.map((digit, index) => digit * ABN_WEIGHTS[index]);
  const summed = weighted.reduce((acc, cur) => acc + cur, 0);
  return summed % 89 === 0;
};
